// ─────────────────────────────────────────────
//  coverageMap — how many rooms each facility needs STAFFED, per time block,
//  per weekday. Configured in Facility Settings, read by Staffing Adequacy.
//
//  ⚠️ NOT THE SAME "COVERAGE" AS `domain/coverage.js`. That module answers
//  "is somebody in THIS room right now" — live, per-room, derived from the board.
//  This one is a CONFIGURED TARGET: per facility, per weekday, per block, typed
//  by an admin. Same English word, opposite direction of travel. When both are on
//  screen, keep them verbally apart.
//
//  THE ONE RELATIONSHIP TO THE TIMELINE CHART: the Staffing Adequacy section
//  compares providers on shift against the coverage number for the block and
//  reports Overcovered / Adequate / Undercovered. Nothing else reads this.
//
//  THE FIVE BLOCKS ARE FIXED AND SHARED BY EVERY FACILITY. Only the numbers are
//  per-facility. An earlier draft made the block list itself per-facility (so
//  Tampa could split its night into 9p-11p + 11p-7a and own an 11 PM boundary);
//  that was reversed on 2026-08-08 — every campus uses the same five, so the
//  boundaries stay the shared 3/5/7/9 PM that `SHIFT_BOUNDARIES` already defines
//  (`timeline.js:46`) and no boundary logic changes at all.
// ─────────────────────────────────────────────

// The five blocks, in clock order. `id` is the Firestore key and never changes;
// the times are display/lookup facts, not stored per facility.
export const COVERAGE_BLOCKS = [
  { id: "b1", label: "7 AM – 3 PM", startMin: 7 * 60, endMin: 15 * 60 },
  { id: "b2", label: "3 PM – 5 PM", startMin: 15 * 60, endMin: 17 * 60 },
  { id: "b3", label: "5 PM – 7 PM", startMin: 17 * 60, endMin: 19 * 60 },
  { id: "b4", label: "7 PM – 9 PM", startMin: 19 * 60, endMin: 21 * 60 },
  // Wraps past midnight: 21:00 → 07:00.
  { id: "b5", label: "9 PM – 7 AM", startMin: 21 * 60, endMin: 7 * 60 },
];

export const COVERAGE_BLOCK_IDS = COVERAGE_BLOCKS.map((b) => b.id);

// `Date.getDay()` order — index 0 is Sunday, so `WEEKDAY_KEYS[d.getDay()]`
// needs no arithmetic. The Setup editor displays Monday-first; that is a
// presentation choice and does not change these keys.
export const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// Weekday key for a `YYYY-MM-DD` roster date key.
//
// ⚠️ `new Date("2026-08-07").getDay()` PARSES AS UTC and is a day out for every
// timezone behind it — which is every US timezone, all year. The whole map is
// keyed by weekday, so that would quietly serve the wrong column for part of
// each day. Split the string and use the local-time constructor.
//
// Mirrors `startOfDayMs` in `hooks/useDailyRoster.js`, deliberately NOT imported
// from it: this module is pure domain and must load without React.
export function weekdayKey(dateKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return WEEKDAY_KEYS[d.getDay()] || null;
}

// Is this facility configured at all? Every facility starts unconfigured, and
// that is a first-class state — not an error, and not a reason to show zeros.
export function hasCoverageMap(facility) {
  const m = facility?.coverageMap;
  if (!m || typeof m !== "object") return false;
  return COVERAGE_BLOCK_IDS.some((id) => {
    const b = m[id];
    return b && typeof b === "object" && WEEKDAY_KEYS.some((d) => Number.isFinite(b[d]));
  });
}

// Rooms needing coverage in one block on one weekday.
//
// Returns `number | null`. **`null` MEANS NOT CONFIGURED and must render as
// nothing** — not `0`, not `—`, not a greyed zero. `0` means a genuine zero (a
// campus that runs no rooms in that block, which is a real answer at weekends
// and overnight) and must read as calm and correct.
//
// Any code shaped `coverageFor(...) || 0` or `?? 0` is a bug: `Number(null) === 0`
// is exactly how "we were never told" becomes "they need nobody".
export function coverageFor(facility, dateKey, blockId) {
  const block = facility?.coverageMap?.[blockId];
  if (!block || typeof block !== "object") return null;
  const day = weekdayKey(dateKey);
  if (!day) return null;
  const v = block[day];
  return Number.isFinite(v) && v >= 0 ? Number(v) : null;
}

// The block that STARTS at a given minute, or null.
//
// This is how a Staffing Adequacy card finds its number. The cards sit on the
// shift boundaries (3/5/7/9 PM) and each boundary is the start of the block that
// follows it, so a 3 PM card reads the 3 PM – 5 PM block. The 7 AM – 3 PM block
// has no boundary card, which is correct: the start of the day is not a shift
// change.
export function blockStartingAt(minuteOfDay) {
  if (minuteOfDay == null) return null;
  return COVERAGE_BLOCKS.find((b) => b.startMin === minuteOfDay) || null;
}

// The block covering a given minute (used for "right now"), handling the
// past-midnight wrap on 9 PM – 7 AM.
export function blockAt(minuteOfDay) {
  if (minuteOfDay == null) return null;
  const t = ((Math.trunc(minuteOfDay) % 1440) + 1440) % 1440;
  return (
    COVERAGE_BLOCKS.find((b) =>
      b.endMin <= b.startMin ? t >= b.startMin || t < b.endMin : t >= b.startMin && t < b.endMin
    ) || null
  );
}

// Providers on shift vs rooms needing coverage.
//
// `unknown` is a distinct outcome and the caller MUST branch on it. A facility
// with no coverage map cannot be judged adequate or inadequate, and saying
// "Adequate" because `needed` defaulted to 0 would be the worst possible answer
// — it reads as a clean bill of health derived from no information at all.
//
// Boundaries: equal counts are ADEQUATE, not short. `delta` is signed and is 0
// when adequate.
export function adequacy(have, needed) {
  const h = Number.isFinite(have) ? Number(have) : null;
  const n = Number.isFinite(needed) ? Number(needed) : null;
  if (h == null || n == null) return { status: "unknown", delta: null, have: h, needed: n };
  const delta = h - n;
  const status = delta > 0 ? "over" : delta === 0 ? "adequate" : "under";
  return { status, delta, have: h, needed: n };
}

// Human wording for an adequacy result. Kept here so the board and any future
// surface cannot drift into two vocabularies for one state.
export function adequacyLabel(result) {
  if (!result || result.status === "unknown") return "No coverage map set";
  if (result.status === "over") return `Overcovered by ${result.delta}`;
  if (result.status === "adequate") return "Adequate";
  return `Undercovered by ${Math.abs(result.delta)}`;
}

// Every block's coverage for a weekday, in clock order. Blocks with no number
// carry `rooms: null` rather than being dropped, so a caller rendering a row per
// block keeps its layout stable instead of reflowing around missing data.
export function coverageRows(facility, dateKey) {
  return COVERAGE_BLOCKS.map((b) => ({
    ...b,
    rooms: coverageFor(facility, dateKey, b.id),
  }));
}
